import { Contract, LedgerStatementItem } from '@starx/shared-types';

export function renderLedgerStatement(
  transactions: LedgerStatementItem[] = [],
  contracts: Contract[] = [],
  activeTab: 'LIVE' | 'DEMO' = 'LIVE'
): string {
  let rows = '';

  if (activeTab === 'LIVE') {
    if (transactions && transactions.length > 0) {
      rows = transactions.map((t) => {
        const isCredit = t.is_credit;
        const amountColor = isCredit ? '#10b981' : '#f43f5e';
        const formattedAmount = `${isCredit ? '+' : '-'}${t.currency} ${parseFloat(t.amount || '0').toFixed(2)}`;
        const isContract = t.reference_id && (t.reference_id.startsWith('C-') || t.reference_id.startsWith('DEMO-C-'));

        return `
          <tr style="border-bottom: 1px solid #1e293b; transition: background 0.15s ease;" onmouseover="this.style.background='#161c28'" onmouseout="this.style.background='transparent'">
            <td style="padding: 10px 12px; font-size: 12px; color: #94a3b8;">${new Date(t.created_at).toLocaleTimeString()}</td>
            <td style="padding: 10px 12px; font-size: 12px; font-weight: 600; color: #f8fafc;">
              <span style="display: block;">${t.entry_type || t.transaction_type}</span>
              <span style="font-size: 10px; color: #64748b;">${t.description}</span>
            </td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #38bdf8;">
              ${t.reference_id}
            </td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; color: ${amountColor};">
              ${formattedAmount}
            </td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #f8fafc; font-weight: 600;">
              ${t.currency} ${parseFloat(t.running_balance || '0').toFixed(2)}
            </td>
            <td style="padding: 10px 12px; font-size: 11px;">
              <span style="background: #10b98122; color: #10b981; border: 1px solid #10b98144; padding: 2px 8px; border-radius: 4px; font-weight: 700;">
                ${t.status || 'POSTED'}
              </span>
            </td>
            <td style="padding: 10px 12px; text-align: right;">
              ${isContract ? `
                <button class="view-statement-receipt-btn" data-contract-id="${t.reference_id}" style="background: #1e293b; color: #38bdf8; border: 1px solid #334155; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer;">
                  📜 Receipt
                </button>
              ` : `<span style="color: #64748b; font-size: 11px;">TX ${t.journal_tx_id.substring(0, 8)}</span>`}
            </td>
          </tr>
        `;
      }).join('');
    } else {
      rows = `
        <tr>
          <td colspan="7" style="padding: 32px 16px; text-align: center; color: #64748b; font-size: 13px;">
            <div style="font-size: 24px; margin-bottom: 8px;">💳</div>
            <div>No recorded live ledger transactions found in this currency.</div>
            <div style="font-size: 11px; color: #475569; margin-top: 4px;">Real deposits, withdrawals, and trade payouts appear here.</div>
          </td>
        </tr>
      `;
    }
  } else {
    // DEMO Tab
    if (contracts && contracts.length > 0) {
      rows = contracts.map((c) => {
        const isWin = c.status === 'SETTLED_WIN';
        const isLoss = c.status === 'SETTLED_LOSS';
        const isCashout = c.status === 'SETTLED_CASHOUT';
        const isTie = c.status === 'SETTLED_TIE';
        const statusColor = isWin ? '#10b981' : isLoss ? '#f43f5e' : isCashout ? '#38bdf8' : '#eab308';
        
        const isRiskFree = Boolean(c.is_risk_free || (c as any).risk_free_token_id);
        const refundNum = isRiskFree ? parseFloat(String((c as any).risk_free_refund_amount || c.risk_free_max_refund || 0)) : 0;
        const stakeNum = parseFloat(String(c.stake || '0'));
        const payoutNum = parseFloat(String(c.actual_payout || '0'));

        let netAmount = '-0.00';
        if (isWin) {
          netAmount = `+${c.currency} ${(payoutNum - stakeNum).toFixed(2)}`;
        } else if (isCashout) {
          netAmount = `+${c.currency} ${payoutNum.toFixed(2)}`;
        } else if (isLoss) {
          if (refundNum >= stakeNum) {
            netAmount = `±${c.currency} 0.00`;
          } else if (refundNum > 0) {
            netAmount = `-${c.currency} ${(stakeNum - refundNum).toFixed(2)}`;
          } else {
            netAmount = `-${c.currency} ${stakeNum.toFixed(2)}`;
          }
        } else if (isTie) {
          netAmount = `±${c.currency} 0.00`;
        }

        return `
          <tr style="border-bottom: 1px solid #1e293b; transition: background 0.15s ease;" onmouseover="this.style.background='#161c28'" onmouseout="this.style.background='transparent'">
            <td style="padding: 10px 12px; font-size: 12px; color: #94a3b8;">${new Date(c.entry_timestamp).toLocaleTimeString()}</td>
            <td style="padding: 10px 12px; font-weight: 600; font-size: 12px; color: #f8fafc;">${c.symbol} ${c.direction}</td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #38bdf8;">${c.contract_id.substring(0, 16)}...</td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; color: ${isWin || isCashout ? '#10b981' : isLoss ? (refundNum >= stakeNum ? '#34d399' : '#f43f5e') : '#94a3b8'};">
              ${netAmount}
            </td>
            <td style="padding: 10px 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #f8fafc;">
              ${c.currency} ${stakeNum.toFixed(2)}
            </td>
            <td style="padding: 10px 12px; font-size: 12px;">
              <span style="background: ${statusColor}22; color: ${statusColor}; border: 1px solid ${statusColor}44; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700;">
                ${c.status}
              </span>
            </td>
            <td style="padding: 10px 12px; text-align: right;">
              <button class="view-statement-receipt-btn" data-contract-id="${c.contract_id}" style="background: #1e293b; color: #38bdf8; border: 1px solid #334155; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer;">
                📜 Receipt
              </button>
            </td>
          </tr>
        `;
      }).join('');
    } else {
      rows = `
        <tr>
          <td colspan="7" style="padding: 32px 16px; text-align: center; color: #64748b; font-size: 13px;">
            <div style="font-size: 24px; margin-bottom: 8px;">🎮</div>
            <div>No recorded demo transactions found.</div>
            <div style="font-size: 11px; color: #475569; margin-top: 4px;">Practice trades executed in demo mode will be listed here.</div>
          </td>
        </tr>
      `;
    }
  }

  return `
    <div class="ledger-statement-modal" style="background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 24px; max-width: 840px; width: 100%; color: #f8fafc; font-family: 'Inter', sans-serif;">
      <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #1e293b; padding-bottom: 16px; margin-bottom: 16px;">
        <div>
          <span style="font-size: 11px; text-transform: uppercase; color: #38bdf8; font-weight: 700; letter-spacing: 0.05em;">Trader Ledger Statement</span>
          <h3 style="margin: 4px 0 0 0; font-size: 18px; color: #ffffff;">Account Audit Trail & Double-Entry Journal</h3>
        </div>
        <button id="close-statement-btn" style="background: #1e293b; color: #94a3b8; border: none; font-size: 16px; border-radius: 6px; width: 32px; height: 32px; cursor: pointer; display: flex; align-items: center; justify-content: center;">
          ✕
        </button>
      </div>

      <!-- Mode Sub-Tabs -->
      <div style="display: flex; gap: 8px; margin-bottom: 14px;">
        <button id="tab-statement-live" style="background: ${activeTab === 'LIVE' ? '#38bdf8' : '#1e293b'}; color: ${activeTab === 'LIVE' ? '#020617' : '#94a3b8'}; border: 1px solid ${activeTab === 'LIVE' ? '#38bdf8' : '#334155'}; padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
          💳 Live Journal (${transactions.length})
        </button>
        <button id="tab-statement-demo" style="background: ${activeTab === 'DEMO' ? '#38bdf8' : '#1e293b'}; color: ${activeTab === 'DEMO' ? '#020617' : '#94a3b8'}; border: 1px solid ${activeTab === 'DEMO' ? '#38bdf8' : '#334155'}; padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
          🎮 Demo Trade History (${contracts.length})
        </button>
      </div>

      <div style="max-height: 420px; overflow-y: auto; border: 1px solid #1e293b; border-radius: 8px; margin-bottom: 16px;">
        <table style="width: 100%; border-collapse: collapse; text-align: left;">
          <thead style="background: #161c28; position: sticky; top: 0; z-index: 10; border-bottom: 1px solid #1e293b;">
            <tr>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">Time</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">${activeTab === 'LIVE' ? 'Entry Type' : 'Direction'}</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">Reference</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">Net P&L / Amount</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">${activeTab === 'LIVE' ? 'Running Balance' : 'Investment'}</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">Status</th>
              <th style="padding: 10px 12px; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700; text-align: right;">Action</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #64748b;">
        <span>All transactions are strictly anchored to PostgreSQL immutable sequence-numbered journal entries.</span>
        <button id="close-statement-footer-btn" style="background: #38bdf8; color: #020617; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer;">
          Close Statement
        </button>
      </div>
    </div>
  `;
}
